/* biome-ignore-all lint/suspicious/noArrayIndexKey: image attachments have no unique IDs */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Card, Table, Select, message, Button, Typography, Tag, Tooltip, Image } from "antd"
import type { ColumnsType } from "antd/es/table"
import type React from "react"
import { useState } from "react"
import { RedoOutlined, LinkOutlined } from "@ant-design/icons"
import { UserCellLink } from "@/components/UserCellLink"
import { DeviceBadge } from "@/components/DeviceBadge"
import { feedbackService, type FeedbackData, type FeedbackStatus } from "@/services/feedbackApi"
import dayjs from "dayjs"

const { Text } = Typography

export const FeedbackPage: React.FC = () => {
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<FeedbackStatus | undefined>()

  const { data, isFetching, refetch } = useQuery({
    queryKey: ["feedbacks_list", page, statusFilter],
    queryFn: () => feedbackService.list({ page, limit: 20, status: statusFilter }),
  })

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: FeedbackStatus }) => feedbackService.updateStatus(id, status),
    onSuccess: () => {
      message.success("Cập nhật trạng thái thành công")
      queryClient.invalidateQueries({ queryKey: ["feedbacks_list"] })
    },
    onError: () => {
      message.error("Lỗi khi cập nhật trạng thái")
    },
  })

  const columns: ColumnsType<FeedbackData> = [
    {
      title: "ID",
      dataIndex: "id",
      width: 70,
      align: "center",
      render: (id) => <span className="font-semibold">{id}</span>,
    },
    {
      title: "Nội dung góp ý / Báo lỗi",
      dataIndex: "message",
      width: 400,
      render: (msg, record) => {
        const rawImages: any = record.images_url?.length ? record.images_url : record.images
        let count = 0
        if (Array.isArray(rawImages)) {
          count = rawImages.length
        } else if (typeof rawImages === "string" && rawImages) {
          try {
            const parsed = JSON.parse(rawImages)
            if (Array.isArray(parsed)) count = parsed.length
          } catch {
            count = 1
          }
        }

        return (
          <Tooltip title={msg} placement="topLeft">
            <div className="line-clamp-3 whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">
              {record.user_agent && (
                <span className="mr-1.5 inline-flex align-middle">
                  <DeviceBadge userAgent={record.user_agent} />
                </span>
              )}
              {msg}
              {count > 0 && (
                <Tag color="cyan" className="ml-1.5 px-1 py-0 font-normal text-[10px]">
                  📷 {count} ảnh
                </Tag>
              )}
            </div>
          </Tooltip>
        )
      },
    },
    {
      title: "Người gửi & Liên hệ",
      key: "reporter",
      width: 220,
      render: (_, record) => {
        const email = record.user?.email || (record.contact?.includes("@") ? record.contact : undefined)
        if (!record.user && !record.name && !email) {
          return null
        }

        return (
          <UserCellLink
            user={record.user}
            userId={record.user_id}
            name={record.name || record.user?.name}
            email={email}
            subtitle={record.contact || record.user?.email}
          />
        )
      },
    },
    {
      title: "Trang phát sinh (URL)",
      dataIndex: "url",
      width: 260,
      ellipsis: true,
      render: (url) => {
        if (!url) return <Text type="secondary">-</Text>
        let displayPath = url
        try {
          const parsed = new URL(url)
          displayPath = parsed.pathname + parsed.search + parsed.hash || "/"
        } catch {
          displayPath = url.replace(/^(?:https?:\/\/)?[^/]+/i, "") || url
        }

        return (
          <Tooltip title={`Mở liên kết: ${url}`}>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 font-mono text-primary text-xs hover:underline"
            >
              <LinkOutlined className="shrink-0 text-xs" />
              <span className="max-w-[220px] truncate">{displayPath}</span>
            </a>
          </Tooltip>
        )
      },
    },
    {
      title: "Thời gian",
      dataIndex: "created_at",
      width: 140,
      align: "center",
      render: (val) => (val ? dayjs(val).format("DD/MM/YYYY HH:mm") : "-"),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      width: 140,
      align: "center",
      render: (status: FeedbackStatus, record) => (
        <Select
          value={status}
          onChange={(newStatus) => updateStatusMutation.mutate({ id: record.id, status: newStatus })}
          options={[
            { label: <Tag color="blue">Mới</Tag>, value: "new" },
            { label: <Tag color="purple">Tiếp nhận</Tag>, value: "in_progress" },
            { label: <Tag color="green">Đã xử lý</Tag>, value: "resolved" },
            { label: <Tag color="volcano">Từ chối</Tag>, value: "rejected" },
          ]}
          size="small"
          className="w-full"
        />
      ),
    },
  ]

  return (
    <div className="flex h-full flex-col gap-2">
      <Card
        className="flex min-h-0 grow flex-col"
        classNames={{ body: "flex grow flex-col" }}
        title={
          <div className="flex w-full items-center justify-between">
            <h2>Báo lỗi / Góp ý (Feedbacks)</h2>
            <div className="flex gap-2">
              <Select
                placeholder="Lọc theo trạng thái"
                allowClear
                style={{ width: 160 }}
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { label: "Mới (New)", value: "new" },
                  { label: "Tiếp nhận (In Progress)", value: "in_progress" },
                  { label: "Đã xử lý (Resolved)", value: "resolved" },
                  { label: "Từ chối (Rejected)", value: "rejected" },
                ]}
              />
              <Button icon={<RedoOutlined />} onClick={() => refetch()} loading={isFetching}>
                Làm mới
              </Button>
            </div>
          </div>
        }
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data?.data || []}
          loading={isFetching}
          pagination={{
            current: page,
            pageSize: 20,
            total: data?.total || 0,
            onChange: (p) => setPage(p),
          }}
          scroll={{ x: 1100, y: "calc(100vh - 250px)" }}
          size="small"
          expandable={{
            expandedRowRender: (record) => {
              const rawImages: any = record.images_url?.length ? record.images_url : record.images
              let imageList: string[] = []
              if (Array.isArray(rawImages)) {
                imageList = rawImages
              } else if (typeof rawImages === "string" && rawImages) {
                try {
                  const parsed = JSON.parse(rawImages)
                  if (Array.isArray(parsed)) imageList = parsed
                } catch {
                  imageList = [rawImages]
                }
              }

              return (
                <div className="flex flex-col gap-3 rounded-lg border border-gray-100 bg-gray-50/80 p-3 text-xs leading-relaxed">
                  <div>
                    <span className="font-semibold text-gray-700">Nội dung chi tiết:</span>
                    <p className="mt-1 whitespace-pre-wrap font-sans text-gray-900 text-sm">{record.message}</p>
                  </div>

                  {imageList.length > 0 && (
                    <div>
                      <span className="mb-1.5 block font-semibold text-gray-700">
                        Ảnh đính kèm ({imageList.length} ảnh - bấm vào để phóng to):
                      </span>
                      <div className="flex flex-wrap items-center gap-2">
                        <Image.PreviewGroup>
                          {imageList.map((imgUrl, idx) => (
                            <Image
                              key={idx}
                              src={imgUrl}
                              alt={`feedback-attachment-${idx}`}
                              width={80}
                              height={80}
                              className="cursor-pointer rounded-lg border border-gray-200 object-cover shadow-xs hover:opacity-90"
                              fallback="data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80' fill='%23ccc'><rect width='80' height='80'/></svg>"
                            />
                          ))}
                        </Image.PreviewGroup>
                      </div>
                    </div>
                  )}

                  {record.url && (
                    <div className="flex items-start gap-1">
                      <span className="shrink-0 font-semibold text-gray-700">URL đầy đủ:</span>
                      <a
                        href={record.url}
                        target="_blank"
                        rel="noreferrer"
                        className="break-all text-primary hover:underline"
                      >
                        {record.url}
                      </a>
                    </div>
                  )}
                  {record.contact && (
                    <div>
                      <span className="font-semibold text-gray-700">Thông tin liên hệ (Contact):</span>
                      <span className="ml-1 font-mono text-gray-800">{record.contact}</span>
                    </div>
                  )}
                  {record.user_agent && (
                    <div className="flex items-center gap-2">
                      <span className="shrink-0 font-semibold text-gray-700">Thiết bị / Nền tảng:</span>
                      <DeviceBadge userAgent={record.user_agent} showDetails />
                    </div>
                  )}
                  {record.user_agent && (
                    <div>
                      <span className="font-semibold text-gray-700">User Agent chi tiết:</span>
                      <span className="ml-1 font-mono text-gray-600 text-xs">{record.user_agent}</span>
                    </div>
                  )}
                  {record.ip_address && (
                    <div>
                      <span className="font-semibold text-gray-700">Địa chỉ IP:</span>
                      <span className="ml-1 font-mono text-gray-700 text-xs">{record.ip_address}</span>
                    </div>
                  )}
                </div>
              )
            },
          }}
        />
      </Card>
    </div>
  )
}

export default FeedbackPage
